import { Fragment, useEffect, useMemo, useState } from "react";
import { Link, useFetcher, useNavigate, useRevalidator, useSearchParams } from "react-router";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination";
import type {
  ResultsLoaderData,
  RobustnessScoreSummary,
  SpecRunTestResult,
} from "@/routes/resultsLoader";
import type { SpecificationsActionResult } from "@/routes/specificationsRoute";
import { format, formatDistanceToNow } from "date-fns";
import { ArrowLeft, Eye, Loader2, PlayCircle } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { getAccuracyColorClass } from "@/lib/utils";

type SelectedResult = {
  type: 'primary' | 'adversarial';
  result: SpecRunTestResult;
  derivativeResult?: SpecRunTestResult['derivative_results'][number];
} | null;

interface RobustnessViewProps {
  projectId: string;
  data: ResultsLoaderData;
  readOnly?: boolean;
}

const MAX_TEXT_LENGTH = 200;

const truncateText = (text: string | null, maxLength = MAX_TEXT_LENGTH): string => {
  if (!text) return "N/A";
  if (text.length <= maxLength) return text;
  return text.slice(0, maxLength) + "…";
};

const formatAttackMethodLabel = (value: string) =>
  value
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");

const formatRunStatus = (status: string | null | undefined) =>
  status ? status.replace(/^[a-z]/, (match) => match.toUpperCase()) : "Unknown";

const formatDate = (dateString?: string | null) => {
  if (!dateString) return "—";
  return formatDistanceToNow(new Date(dateString), { addSuffix: true });
};

const formatTimestamp = (dateString?: string | null) => {
  if (!dateString) return "—";
  return format(new Date(dateString), "PPp");
};

const getAdversarialDatasetIds = (
  specRun: ResultsLoaderData["selectedSpecRun"] | null | undefined,
): string[] => {
  if (!specRun) return [];
  const datasetIds = new Set<string>();

  specRun.robustness_scores.forEach((score) => {
    datasetIds.add(score.derivative_dataset_id);
  });

  specRun.derivative_runs.forEach(({ run, dataset }) => {
    const datasetId = run.test_data_set_id ?? dataset?.id;
    if (datasetId) {
      datasetIds.add(datasetId);
    }
  });

  return Array.from(datasetIds);
};

export const RobustnessView = ({
  projectId,
  data,
  readOnly = false,
}: RobustnessViewProps) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const selectedSpecRunId = searchParams.get("specRunId");
  const selectedSpecRun = data.selectedSpecRun;
  const { toast } = useToast();
  const revalidator = useRevalidator();
  const [selectedResult, setSelectedResult] = useState<SelectedResult>(null);

  type AdversarialCard = {
    datasetId: string;
    datasetName: string;
    creationMethod: string | null;
    runStatus: string | null;
    runId: string | null;
    score: RobustnessScoreSummary | null;
  };

  const adversarialCards = useMemo((): AdversarialCard[] => {
    if (!selectedSpecRun) return [];

    const scoreByDatasetId = new Map<string, RobustnessScoreSummary>(
      selectedSpecRun.robustness_scores.map((score) => [
        score.derivative_dataset_id,
        score,
      ]),
    );

    const cards: AdversarialCard[] = [];
    const seenDatasetIds = new Set<string>();

    selectedSpecRun.derivative_runs.forEach(({ run, dataset }) => {
      const datasetId = run.test_data_set_id ?? dataset?.id;
      if (!datasetId) return;

      seenDatasetIds.add(datasetId);
      const score = scoreByDatasetId.get(datasetId) ?? null;

      cards.push({
        datasetId,
        datasetName: dataset?.name ?? score?.derivative_dataset_name ?? "Unknown",
        creationMethod:
          dataset?.creation_method ?? score?.derivative_creation_method ?? null,
        runStatus: run.status ?? null,
        runId: run.id ?? null,
        score,
      });
    });

    selectedSpecRun.robustness_scores.forEach((score) => {
      if (seenDatasetIds.has(score.derivative_dataset_id)) return;

      cards.push({
        datasetId: score.derivative_dataset_id,
        datasetName: score.derivative_dataset_name,
        creationMethod: score.derivative_creation_method,
        runStatus: null,
        runId: null,
        score,
      });
    });

    return cards;
  }, [selectedSpecRun]);
  
  // Initialize enabled datasets with all adversarial dataset IDs
  const [enabledDatasets, setEnabledDatasets] = useState<Set<string>>(() => {
    return new Set(getAdversarialDatasetIds(data.selectedSpecRun));
  });

  // Update enabled datasets when selectedSpecRun changes
  useEffect(() => {
    setEnabledDatasets(new Set(getAdversarialDatasetIds(data.selectedSpecRun)));
  }, [data.selectedSpecRun]);

  // Pagination for per-test-case table
  const PAGE_SIZE = 5;
  const [currentPage, setCurrentPage] = useState(1);
  const testResults = useMemo(
    () => selectedSpecRun?.test_results ?? [],
    [selectedSpecRun],
  );
  const totalPages = Math.ceil(testResults.length / PAGE_SIZE);
  const paginatedTestResults = useMemo(() => {
    const start = (currentPage - 1) * PAGE_SIZE;
    return testResults.slice(start, start + PAGE_SIZE);
  }, [testResults, currentPage]);

  // Reset to page 1 when spec run changes
  useEffect(() => {
    setCurrentPage(1);
  }, [selectedSpecRunId]);

  const runFetcher = useFetcher<SpecificationsActionResult>({
    key: "robustness-run-spec",
  });

  const isRunning = runFetcher.state !== "idle";
  const runningSpecId =
    isRunning && runFetcher.formData
      ? runFetcher.formData.get("specificationId")?.toString()
      : null;

  useEffect(() => {
    if (runFetcher.state === "idle" && runFetcher.data) {
      const result = runFetcher.data;
      if (result.intent === "specificationRun") {
        if (result.ok) {
          toast({
            title: "Specification run started",
            description: "The specification run has been queued.",
          });
          revalidator.revalidate();
        } else {
          toast({
            title: "Failed to start run",
            description: result.error ?? "An error occurred",
            variant: "destructive",
          });
        }
      }
    }
  }, [runFetcher.state, runFetcher.data, toast, revalidator]);

  const handleRunSpecification = (specificationId: string) => {
    if (readOnly) {
      toast({
        title: "Read-only",
        description: "You don't have permission to rerun specifications here.",
        variant: "destructive",
      });
      return;
    }

    const formData = new FormData();
    formData.set("intent", "specificationRun");
    formData.set("specificationId", specificationId);
    runFetcher.submit(formData, {
      method: "post",
      action: `/workspace/evals/${projectId}/specifications`,
    });
  };

  const specificationCards = useMemo(
    () => data.specifications ?? [],
    [data.specifications],
  );

  if (selectedSpecRun && selectedSpecRunId) {
    const { specification, spec_run, primary_dataset, robustness_scores } =
      selectedSpecRun;

    return (
      <div className="space-y-6" data-testid="robustness-view-detail">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate(`/workspace/evals/${projectId}/results`)}
            className="p-2"
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div className="space-y-1">
            <h2 className="text-2xl font-bold text-foreground">
              Robustness Results
            </h2>
            <p className="text-sm text-muted-foreground">
              {specification?.name ?? "Specification"} · Run {spec_run.id}
            </p>
          </div>
        </div>

        <Card>
          <CardContent className="p-6 space-y-4">
            <div className="flex flex-wrap gap-4">
              <div className="flex-1 min-w-[200px]">
                <p className="text-xs text-muted-foreground">Status</p>
                <p className="text-sm font-medium">
                  {formatRunStatus(spec_run.status)}
                </p>
              </div>
              <div className="flex-1 min-w-[200px]">
                <p className="text-xs text-muted-foreground">Started</p>
                <p className="text-sm font-medium">
                  {formatTimestamp(spec_run.started_at)}
                </p>
              </div>
              <div className="flex-1 min-w-[200px]">
                <p className="text-xs text-muted-foreground">Completed</p>
                <p className="text-sm font-medium">
                  {formatTimestamp(spec_run.completed_at)}
                </p>
              </div>
            </div>
            {primary_dataset && (
              <div>
                <p className="text-xs text-muted-foreground">Primary dataset</p>
                <p className="text-sm font-medium">{primary_dataset.name}</p>
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <h3 className="text-lg font-semibold">Robustness</h3>
            <p className="text-sm text-muted-foreground">
              Comparison of primary run to adversarial runs
            </p>
          </CardHeader>
          <CardContent>
            {robustness_scores.length === 0 && !selectedSpecRun.primary_run ? (
              <Alert>
                <AlertDescription>
                  No robustness scores yet. Ensure all runs have completed.
                </AlertDescription>
              </Alert>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Primary Run Summary */}
                {selectedSpecRun.primary_run && (
                  <div className="rounded-lg border border-border bg-muted/30 p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium">
                          {primary_dataset?.name ?? "Primary Dataset"}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          Primary · {selectedSpecRun.test_results.length} test cases
                        </p>
                      </div>
                      <Badge variant="outline" className={getAccuracyColorClass(selectedSpecRun.stats.correct)}>
                        {selectedSpecRun.stats.correct}% correct
                      </Badge>
                    </div>
                    <div className="mt-3 text-xs text-muted-foreground">
                      <span className="font-medium">Status:</span>{" "}
                      {formatRunStatus(selectedSpecRun.primary_run.status)}
                      {selectedSpecRun.primary_run.end_time && (
                        <span className="ml-4">
                          <span className="font-medium">Completed:</span>{" "}
                          {formatDate(selectedSpecRun.primary_run.end_time)}
                        </span>
                      )}
                    </div>
                  </div>
                )}

                {/* Adversarial Robustness Scores */}
                {adversarialCards.map((card) => {
                  const score = card.score;

                  return (
                    <div
                      key={card.runId ?? card.datasetId}
                      className="rounded-lg border border-border p-4"
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm font-medium">
                            {card.datasetName}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {card.creationMethod
                              ? formatAttackMethodLabel(card.creationMethod)
                              : "Adversarial"}
                          </p>
                        </div>
                        {!score ? (
                          <Badge
                            variant={
                              card.runStatus === "Failed"
                                ? "destructive"
                                : "secondary"
                            }
                          >
                            {formatRunStatus(card.runStatus)}
                          </Badge>
                        ) : (
                          <Badge
                            variant="outline"
                            className={getAccuracyColorClass(score.robust_accuracy)}
                          >
                            {Math.round(score.robust_accuracy)}%
                          </Badge>
                        )}
                      </div>

                      <div className="mt-3 flex items-end justify-between gap-2">
                        {score?.metrics ? (
                          <div className="text-xs text-muted-foreground">
                            Robust cases:{" "}
                            {typeof score.metrics["robust_count"] === "number"
                              ? score.metrics["robust_count"]
                              : "—"}{" "}
                            /{" "}
                            {typeof score.metrics["total_primary_entries"] === "number"
                              ? score.metrics["total_primary_entries"]
                              : "—"}
                          </div>
                        ) : card.runStatus ? (
                          <div className="text-xs text-muted-foreground">
                            Status: {formatRunStatus(card.runStatus)}
                          </div>
                        ) : null}

                        <div className="flex items-center gap-2 ml-auto">
                          {card.runStatus === "Failed" && card.runId ? (
                            <Button
                              asChild
                              variant="ghost"
                              size="sm"
                              className="h-7 px-2 text-xs"
                            >
                              <Link
                                to={`/workspace/test-runs/results?testRunId=${card.runId}`}
                              >
                                View run
                              </Link>
                            </Button>
                          ) : null}
                          <div className="flex items-center gap-1.5">
                            <label
                              htmlFor={`filter-${card.datasetId}`}
                              className="text-xs text-muted-foreground cursor-pointer"
                            >
                              Show
                            </label>
                            <Checkbox
                              id={`filter-${card.datasetId}`}
                              checked={enabledDatasets.has(card.datasetId)}
                              onCheckedChange={(checked) => {
                                setEnabledDatasets((prev) => {
                                  const next = new Set(prev);
                                  if (checked) {
                                    next.add(card.datasetId);
                                  } else {
                                    next.delete(card.datasetId);
                                  }
                                  return next;
                                });
                              }}
                              className="border-muted-foreground/50 data-[state=checked]:bg-background data-[state=checked]:text-muted-foreground"
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <h3 className="text-lg font-semibold">Per-test-case robustness</h3>
            <p className="text-sm text-muted-foreground">
              Primary results with all adversarial variants from this specification
              run
            </p>
          </CardHeader>
          <CardContent>
            {selectedSpecRun.test_results.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                No test results found.
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-1/5 min-w-[200px]">
                        Input
                      </TableHead>
                      <TableHead className="w-1/5 min-w-[200px]">
                        Expected Output
                      </TableHead>
                      <TableHead className="w-1/5 min-w-[200px]">
                        Actual Output
                      </TableHead>
                      <TableHead className="w-1/6 min-w-[100px]">
                        Correctness
                      </TableHead>
                      <TableHead className="w-1/6 min-w-[140px]">
                        Robustness
                      </TableHead>
                      <TableHead className="w-24 min-w-[80px]">
                        Actions
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {paginatedTestResults.map((result) => {
                      const primaryCorrect = result.correctness === 100;
                      const robustnessRatio =
                        result.robustness_count > 0
                          ? result.robustness_passed / result.robustness_count
                          : null;
                      const robustnessPercent =
                        robustnessRatio !== null
                          ? Math.round(robustnessRatio * 100)
                          : null;
                      const robustnessColor =
                        robustnessRatio !== null
                          ? getAccuracyColorClass(robustnessPercent ?? 0)
                          : "text-muted-foreground";

                      return (
                        <Fragment key={result.id}>
                          <TableRow className="bg-muted/20">
                            <TableCell className="text-sm align-top">
                              <div className="flex items-start gap-2">
                                <Badge variant="outline" className="text-xs">
                                  Primary
                                </Badge>
                                <div className="max-w-none break-words whitespace-normal">
                                  {truncateText(result.input)}
                                </div>
                              </div>
                            </TableCell>
                            <TableCell className="text-sm align-top">
                              <div className="max-w-none break-words whitespace-normal">
                                {truncateText(result.expected_output)}
                              </div>
                            </TableCell>
                            <TableCell className="text-sm align-top">
                              <div className="max-w-none break-words whitespace-normal">
                                {truncateText(result.output)}
                              </div>
                            </TableCell>
                            <TableCell className="text-sm align-top">
                            {result.correctness !== null ? (
                                <span
                                  className={`font-medium ${getAccuracyColorClass(result.correctness)}`}
                                >
                                  {result.correctness}%
                                </span>
                              ) : (
                                <span className="text-xs text-muted-foreground">
                                  N/A
                                </span>
                              )}
                            </TableCell>
                            <TableCell className="text-sm align-top">
                              {!primaryCorrect ? (
                                <span className="text-xs text-red-600 dark:text-red-400">
                                  Primary failed
                                </span>
                              ) : result.robustness_count === 0 ? (
                                <span className="text-xs text-muted-foreground">
                                  No adversarial variants
                                </span>
                              ) : (
                                <div className="flex flex-col gap-1">
                                  <span className={`font-medium ${robustnessColor}`}>
                                    {result.robustness_passed} /{" "}
                                    {result.robustness_count}
                                  </span>
                                  <span className="text-xs text-muted-foreground">
                                    {robustnessPercent}% robust
                                  </span>
                                </div>
                              )}
                            </TableCell>
                            <TableCell className="text-sm align-top">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => setSelectedResult({
                                  type: 'primary',
                                  result: result,
                                })}
                              >
                                View Details
                              </Button>
                            </TableCell>
                          </TableRow>
                          {result.derivative_results
                            .filter((derivative) => enabledDatasets.has(derivative.dataset_id))
                            .map((derivative) => (
                            <TableRow key={derivative.id}>
                              <TableCell className="text-sm align-top pl-8">
                                <div className="flex items-start gap-2">
                                  <Badge variant="secondary" className="text-xs">
                                    {derivative.creation_method
                                      ? formatAttackMethodLabel(derivative.creation_method)
                                      : "Adversarial"}
                                  </Badge>
                                  <div className="max-w-none break-words whitespace-normal text-muted-foreground">
                                    {truncateText(derivative.input)}
                                  </div>
                                </div>
                              </TableCell>
                              <TableCell className="text-sm align-top">
                                <div className="max-w-none break-words whitespace-normal text-muted-foreground">
                                  {truncateText(derivative.expected_output)}
                                </div>
                              </TableCell>
                              <TableCell className="text-sm align-top">
                                <div className="max-w-none break-words whitespace-normal">
                                  {truncateText(derivative.output)}
                                </div>
                              </TableCell>
                              <TableCell className="text-sm align-top">
                              {derivative.correctness !== null ? (
                                  <span
                                    className={`font-medium ${getAccuracyColorClass(derivative.correctness)}`}
                                  >
                                    {derivative.correctness}%
                                  </span>
                                ) : (
                                  <span className="text-xs text-muted-foreground">
                                    N/A
                                  </span>
                                )}
                              </TableCell>
                              <TableCell className="text-sm align-top text-muted-foreground">
                                —
                              </TableCell>
                              <TableCell className="text-sm align-top">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => setSelectedResult({
                                    type: 'adversarial',
                                    result: result,
                                    derivativeResult: derivative,
                                  })}
                                >
                                  View Details
                                </Button>
                              </TableCell>
                            </TableRow>
                          ))}
                        </Fragment>
                      );
                    })}
                  </TableBody>
                </Table>
                {totalPages > 1 && (
                  <Pagination className="mt-6">
                    <PaginationContent>
                      <PaginationItem>
                        <PaginationPrevious
                          href="#"
                          onClick={(e) => {
                            e.preventDefault();
                            setCurrentPage((p) => Math.max(1, p - 1));
                          }}
                          className={currentPage === 1 ? "pointer-events-none opacity-50" : "cursor-pointer"}
                        />
                      </PaginationItem>
                      {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                        <PaginationItem key={page}>
                          <PaginationLink
                            href="#"
                            isActive={page === currentPage}
                            onClick={(e) => {
                              e.preventDefault();
                              setCurrentPage(page);
                            }}
                            className="cursor-pointer"
                          >
                            {page}
                          </PaginationLink>
                        </PaginationItem>
                      ))}
                      <PaginationItem>
                        <PaginationNext
                          href="#"
                          onClick={(e) => {
                            e.preventDefault();
                            setCurrentPage((p) => Math.min(totalPages, p + 1));
                          }}
                          className={currentPage === totalPages ? "pointer-events-none opacity-50" : "cursor-pointer"}
                        />
                      </PaginationItem>
                    </PaginationContent>
                  </Pagination>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Test Result Details Dialog */}
        <Dialog
          open={!!selectedResult}
          onOpenChange={() => setSelectedResult(null)}
        >
          <DialogContent className="max-w-3xl max-h-[80vh]">
            <DialogHeader>
              <DialogTitle>
                {selectedResult?.type === 'primary' ? 'Primary' : 'Adversarial'} Test Result Details
              </DialogTitle>
              <DialogDescription>
                {selectedResult?.type === 'adversarial' && selectedResult.derivativeResult
                  ? `Dataset: ${selectedResult.derivativeResult.dataset_name}`
                  : primary_dataset?.name ?? 'Primary Dataset'}
              </DialogDescription>
            </DialogHeader>
            <ScrollArea className="h-[60vh]">
              {selectedResult && (
                <div className="space-y-4 pr-4">
                  {/* Summary Box */}
                  <div className="rounded-lg border border-border bg-muted/30 p-4 flex flex-wrap gap-4">
                    {selectedResult.type === 'adversarial' && selectedResult.derivativeResult && (
                      <div className="flex-1 min-w-[120px]">
                        <p className="text-xs text-muted-foreground mb-1">Attack Method</p>
                        <Badge variant="secondary">
                          {formatAttackMethodLabel(selectedResult.derivativeResult.creation_method ?? 'Unknown')}
                        </Badge>
                      </div>
                    )}
                    {selectedResult.type === 'primary' && selectedResult.result.robustness_count > 0 && (
                      <div className="flex-1 min-w-[120px]">
                        <p className="text-xs text-muted-foreground mb-1">Robustness</p>
                        <p className="text-sm font-medium">
                          {selectedResult.result.robustness_passed} / {selectedResult.result.robustness_count} passed
                        </p>
                      </div>
                    )}
                    <div className="flex-1 min-w-[120px]">
                      <p className="text-xs text-muted-foreground mb-1">Correctness</p>
                      {(() => {
                        const correctnessValue = selectedResult.type === 'adversarial' && selectedResult.derivativeResult
                          ? selectedResult.derivativeResult.correctness
                          : selectedResult.result.correctness;
                        return correctnessValue !== null ? (
                          <Badge variant="outline" className={getAccuracyColorClass(correctnessValue)}>
                            {correctnessValue}%
                          </Badge>
                        ) : (
                          <span className="text-muted-foreground text-sm">N/A</span>
                        );
                      })()}
                    </div>
                  </div>

                  <div>
                    <h4 className="font-semibold mb-2">Input</h4>
                    <pre className="text-sm bg-muted p-3 rounded overflow-x-auto whitespace-pre-wrap">
                      {selectedResult.type === 'adversarial' && selectedResult.derivativeResult
                        ? selectedResult.derivativeResult.input
                        : selectedResult.result.input ?? 'N/A'}
                    </pre>
                  </div>
                  
                  <div>
                    <h4 className="font-semibold mb-2">Expected Output</h4>
                    <pre className="text-sm bg-muted p-3 rounded overflow-x-auto whitespace-pre-wrap">
                      {selectedResult.type === 'adversarial' && selectedResult.derivativeResult
                        ? selectedResult.derivativeResult.expected_output
                        : selectedResult.result.expected_output ?? 'N/A'}
                    </pre>
                  </div>
                  
                  <div>
                    <h4 className="font-semibold mb-2">Actual Output</h4>
                    <pre className="text-sm bg-muted p-3 rounded overflow-x-auto whitespace-pre-wrap">
                      {selectedResult.type === 'adversarial' && selectedResult.derivativeResult
                        ? selectedResult.derivativeResult.output
                        : selectedResult.result.output}
                    </pre>
                  </div>
                </div>
              )}
            </ScrollArea>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="robustness-view-list">
      <div className="space-y-1">
        <h2 className="text-2xl font-bold text-foreground">Robustness</h2>
        <p className="text-sm text-muted-foreground">
          Robustness results are scoped to specification runs.
        </p>
      </div>

      {specificationCards.length === 0 ? (
        <Card>
          <CardContent className="py-10 text-center text-sm text-muted-foreground">
            No specification runs yet. Run a specification to see robustness
            results.
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {specificationCards.map((spec) => {
            const primaryDataset =
              spec.specification_datasets?.find(
                (dataset) => dataset.role === "primary",
              )?.test_data_sets ?? null;
            const derivativeCount =
              spec.specification_datasets?.filter(
                (dataset) => dataset.role === "adversarial",
              ).length ?? 0;
            const latestRun = spec.latest_run;

            return (
              <Card key={spec.id} data-testid={`robustness-spec-${spec.id}`}>
                <CardContent className="p-6 space-y-4">
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <h3 className="text-lg font-semibold">{spec.name}</h3>
                      {spec.description && (
                        <p className="text-sm text-muted-foreground">
                          {spec.description}
                        </p>
                      )}
                    </div>
                    {latestRun ? (
                      <Badge variant="outline">
                        {formatRunStatus(latestRun.status)}
                      </Badge>
                    ) : (
                      <Badge variant="secondary">No runs</Badge>
                    )}
                  </div>

                  <div className="grid gap-3 md:grid-cols-2">
                    <div>
                      <p className="text-xs text-muted-foreground">Primary</p>
                      <p className="text-sm font-medium">
                        {primaryDataset?.name ?? "Unknown"}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Adversarial</p>
                      <p className="text-sm font-medium">
                        {derivativeCount}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center justify-between">
                    <div className="text-xs text-muted-foreground">
                      {latestRun
                        ? `Last run ${formatDate(latestRun.started_at)}`
                        : "No runs yet"}
                    </div>
                    <div className="flex items-center gap-2">
                      {latestRun?.status === "Complete" && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleRunSpecification(spec.id)}
                          disabled={readOnly || runningSpecId === spec.id}
                          data-testid={`rerun-spec-${spec.id}`}
                        >
                          {runningSpecId === spec.id ? (
                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          ) : (
                            <PlayCircle className="h-4 w-4 mr-2" />
                          )}
                          Rerun spec
                        </Button>
                      )}
                      {latestRun ? (
                        <Button asChild size="sm" variant="outline">
                          <Link
                            to={`/workspace/evals/${projectId}/results?specRunId=${latestRun.id}`}
                          >
                            <Eye className="h-4 w-4 mr-2" />
                            View robustness
                          </Link>
                        </Button>
                      ) : null}
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {selectedSpecRunId && !selectedSpecRun && (
        <Alert>
          <AlertDescription>
            The requested specification run could not be found. Please select
            another run.
          </AlertDescription>
        </Alert>
      )}
    </div>
  );
};
